import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { APP_TOKEN_KEY } from "./appAuthSlice";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const apiMessage = (error, fallback) =>
  error.response?.data?.error ||
  error.response?.data?.message ||
  error.message ||
  fallback;

export const fetchCoinPoolConstants = createAsyncThunk(
  "coinPool/fetchConstants",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/coin-pool/constants");
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load Coin Pool rules"));
    }
  }
);

export const fetchCoinPoolCurrentRound = createAsyncThunk(
  "coinPool/fetchCurrentRound",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/coin-pool/rounds/current");
      return res.data?.round ?? null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load Coin Pool round"));
    }
  }
);

export const fetchCoinPoolHistory = createAsyncThunk(
  "coinPool/fetchHistory",
  async ({ limit = 20, since, until } = {}, { rejectWithValue }) => {
    try {
      const params = { limit };
      if (since) params.since = since;
      if (until) params.until = until;
      const res = await api.get("/api/coin-pool/rounds/history", { params });
      return {
        rounds: res.data?.rounds || [],
        total: Number(res.data?.total) || (res.data?.rounds || []).length,
      };
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load pool history"));
    }
  }
);

export const coinPoolJoinRound = createAsyncThunk(
  "coinPool/joinRound",
  async ({ side, stake, amount, fillMode, joinIdempotencyKey, idempotencyKey }, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/coin-pool/orders", {
        side,
        amount: amount ?? stake,
        fillMode,
        idempotencyKey: idempotencyKey || joinIdempotencyKey,
      });
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not place Coin Pool order"));
    }
  }
);

export const coinPoolPlaceOrder = coinPoolJoinRound;

export const coinPoolCancelOrder = createAsyncThunk(
  "coinPool/cancelOrder",
  async ({ orderId }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/coin-pool/orders/${orderId}/cancel`);
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not cancel order"));
    }
  }
);

const initialState = {
  constants: null,
  round: null,
  lastResultRound: null,
  history: [],
  historyTotal: 0,
  loading: false,
  actionLoading: false,
  error: null,
};

const coinPoolSlice = createSlice({
  name: "coinPool",
  initialState,
  reducers: {
    setCoinPoolRound: (state, { payload }) => {
      state.round = payload || null;
    },
    setCoinPoolLastResult: (state, { payload }) => {
      state.lastResultRound = payload || null;
    },
    clearCoinPoolError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCoinPoolConstants.fulfilled, (state, { payload }) => {
        state.constants = payload;
      })
      .addCase(fetchCoinPoolCurrentRound.pending, (state) => {
        if (!state.round) state.loading = true;
      })
      .addCase(fetchCoinPoolCurrentRound.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.round = payload;
      })
      .addCase(fetchCoinPoolCurrentRound.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(coinPoolJoinRound.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(coinPoolJoinRound.fulfilled, (state, { payload }) => {
        state.actionLoading = false;
        if (payload?.round) state.round = payload.round;
      })
      .addCase(coinPoolJoinRound.rejected, (state, { payload }) => {
        state.actionLoading = false;
        state.error = payload;
      })
      .addCase(coinPoolCancelOrder.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(coinPoolCancelOrder.fulfilled, (state, { payload }) => {
        state.actionLoading = false;
        if (payload?.round) state.round = payload.round;
      })
      .addCase(coinPoolCancelOrder.rejected, (state, { payload }) => {
        state.actionLoading = false;
        state.error = payload;
      })
      .addCase(fetchCoinPoolHistory.fulfilled, (state, { payload }) => {
        if (Array.isArray(payload)) {
          state.history = payload;
          state.historyTotal = payload.length;
          return;
        }
        state.history = payload?.rounds || [];
        state.historyTotal = Number(payload?.total) || state.history.length;
      });
  },
});

export const { setCoinPoolRound, setCoinPoolLastResult, clearCoinPoolError } =
  coinPoolSlice.actions;

export const selectCoinPoolRound = (state) => state.coinPool.round;
export const selectCoinPoolConstants = (state) => state.coinPool.constants;
export const selectCoinPoolLoading = (state) => state.coinPool.loading;
export const selectCoinPoolActionLoading = (state) => state.coinPool.actionLoading;
export const selectCoinPoolHistory = (state) => state.coinPool.history || [];
export const selectCoinPoolHistoryTotal = (state) =>
  state.coinPool.historyTotal ?? state.coinPool.history?.length ?? 0;

export default coinPoolSlice.reducer;
